/**
 * `GET /api/tracking/portal/action-plan/[?building=id,id]` (G1,
 * `tracking/services/action_items.py`). The server owns every date and
 * sum; this codec only renames and types. Money stays `null` when the wire
 * says nothing, never 0.
 */
import * as React from "react";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { z } from "zod";

import { codecGet, isObject } from "@/api/codec";
import {
  usePortalFeaturesQuery,
  type PortalFeatures,
} from "./usePortalFeatures";

export type MoneyKind = "owed" | "charged" | "on_record" | "estimate" | "none";
/** action_items.MONEY_ESTIMATE: an "about" row; its usd is null and the figure is `estimateUsd` (Leo 10-27). */
export const MONEY_ESTIMATE = "estimate";
/** action_items.py boiler and DEP kinds (Leo 10-27). */
export const KIND_BOILER_INSPECT = "boiler_inspect";
export const KIND_BOILER_REINSPECT = "boiler_reinspect";
export const KIND_BOILER_REPORT_MISSED = "boiler_report_missed";
export const KIND_DEP_REGISTRATION = "dep_registration";
/** action_items.FLOW_KINDS: no Balance, never in a total. */
export const FLOW_KINDS: ReadonlySet<string> = new Set([
  KIND_BOILER_INSPECT,
  KIND_BOILER_REINSPECT,
  KIND_BOILER_REPORT_MISSED,
  KIND_DEP_REGISTRATION,
]);

/** Flight deck fields (contract 2026-10-08, `test_action_plan_contract.py`).
 * Absent on the wire until the server sends them: default, never fail. */
export type RailState = "past" | "x" | "due" | "open" | "sent";
/** Lane keys (action_plan_lanes.LANES); an agency lane's key is also its `agency`. */
export const LANE_HPD = "HPD";
export const LANE_DOB = "DOB";
export const LANE_LL33 = "LL33";
/** An unattributed row's agency (tracking/constants/agencies.py AGENCY_UNKNOWN). */
export const AGENCY_UNKNOWN = "UNKNOWN";
/** Issuer codes the FE keys on, minted in tracking/constants/agencies.py. */
export const AGENCY_DEP = "DEP";
export const AGENCY_DOHMH = "DOHMH";
export const AGENCY_DOT = "DOT";
export interface ItemEdge {
  to: string;
  kind: "leg_next";
  label: string;
}
export interface ItemHandoff {
  action: "authorize" | "hand" | "ask";
  /** engaged = staff logged work; Undo then 409s. */
  state: "none" | "requested" | "withdrawn" | "engaged";
  at: string | null;
  /** On hand: who signed the covering authorization ("name, title, capacity"). */
  byName: string | null;
  /** ISO date the covering authorization was signed. */
  authorizedOn: string | null;
  /** What CodeClear does on this kind (handoff.WILL); null where it cannot take it. */
  will: string | null;
  /** The confirm's "You still" (handoff.you_still); hearing kinds name who appears. */
  youStill: string | null;
  /** Only hand + requested. */
  undoable: boolean;
  /** "CodeClear next: ..." on a handed item. */
  next: string | null;
  /** Authorize: the respondent on the tickets to sign for. */
  owner: { entity: string; key: string } | null;
}
/** "agency" reads "With HPD": certified, nothing to hand off. */
export type ItemOwner = "you" | "codeclear" | "agency";

/** Debt Demolisher (LL147 of 2026) on an owed item: price over `priced` tickets, `unknown` with none. */
export interface ItemAmnesty {
  priceUsd: number | null;
  certificateUsd: number | null;
  priced: number;
  unknown: number;
}

/**
 * Feed key for `portal_judgment_owed`, the features wire's `JUDGMENT_OWED_KEY` (Leo 10-31). The same string on an
 * item marks a judgment unit (ruling 2). Surfaces that render this feed branch on `feed.judgmentOwed` only; surfaces
 * with no feed (case page, 1E) read `usePortalFeatures().judgmentOwed`.
 */
export const JUDGMENT_OWED_KEY = "judgment_owed";
/** Which judgment row (ruling 17); 2A's action_items.JUDGMENT_STEP_* must write these exact values. Unknown reads null. */
export const JUDGMENT_STEP_PAY = "pay";
export const JUDGMENT_STEP_FDNY_CERT = "fdny_cert";
export const JUDGMENT_STEP_DOB_CERT = "dob_cert";
/** Sat Jan 9 and Sun Jan 10 (server `price_late`): a certificate row pays if the agency accepted. */
export const JUDGMENT_STEP_FDNY_IF_ACCEPTED = "fdny_if_accepted";
export const JUDGMENT_STEP_DOB_IF_ACCEPTED = "dob_if_accepted";
export type JudgmentStep =
  | typeof JUDGMENT_STEP_PAY
  | typeof JUDGMENT_STEP_FDNY_CERT
  | typeof JUDGMENT_STEP_DOB_CERT
  | typeof JUDGMENT_STEP_FDNY_IF_ACCEPTED
  | typeof JUDGMENT_STEP_DOB_IF_ACCEPTED;
export const JUDGMENT_STEPS: readonly JudgmentStep[] = [
  JUDGMENT_STEP_PAY,
  JUDGMENT_STEP_FDNY_CERT,
  JUDGMENT_STEP_DOB_CERT,
  JUDGMENT_STEP_FDNY_IF_ACCEPTED,
  JUDGMENT_STEP_DOB_IF_ACCEPTED,
];
/** Whose certificate a correction-first row needs (Leo 10-31 C1); one map for bars, rows and rail. */
export const CERT_AGENCY: Partial<Record<string, string>> = {
  [JUDGMENT_STEP_FDNY_CERT]: "FDNY",
  [JUDGMENT_STEP_DOB_CERT]: "DOB",
  [JUDGMENT_STEP_FDNY_IF_ACCEPTED]: "FDNY",
  [JUDGMENT_STEP_DOB_IF_ACCEPTED]: "DOB",
};
const PAY_CLOCK = "Amnesty price ends";
/** A judgment step's clock word (C9: the pay row's says the price ends, never a filing deadline); the rail meter and
 * the case page's Next deadline cell read it. Server twin: action_plan_judgment.CLOCK_WORD. */
export const JUDGMENT_CLOCK_WORD: Record<JudgmentStep, string> = {
  [JUDGMENT_STEP_PAY]: PAY_CLOCK,
  [JUDGMENT_STEP_FDNY_CERT]: "File certificate",
  [JUDGMENT_STEP_DOB_CERT]: "DOB acceptance",
  [JUDGMENT_STEP_FDNY_IF_ACCEPTED]: PAY_CLOCK,
  [JUDGMENT_STEP_DOB_IF_ACCEPTED]: PAY_CLOCK,
};
export const certAgency = (item: { judgmentStep?: string | null }): string | null =>
  (item.judgmentStep && CERT_AGENCY[item.judgmentStep]) || null;
/** FDNY Cure Desk auto-reply, Oct 15 2026 (SR BFP0373264); relief_programs.FDNY_CERTIFICATE_REVIEW_DAYS. */
export const FDNY_REVIEW_DAYS = 60;

export interface ActionItem {
  key: string;
  kind: string;
  buildingId: string;
  address: string;
  agency: string;
  agencyCounts: Record<string, number>;
  recordIds: string[];
  /** "boiler" on a missed filing: the ids are devices, not tickets. */
  recordNoun: string;
  /** Missed boilers a DOB violation covers (DA3, 14-50 Gateway Y0004). */
  boilerIds: string[];
  action: string;
  subject: string;
  subjects: string[];
  actBy: string | null;
  actByCalendar: string | null;
  actByLabel: string | null;
  est: boolean;
  money: {
    kind: MoneyKind;
    usd: number | null;
    chargedUsd: number | null;
    interestPerDayUsd: number | null;
    /** Wire keys of 10-21 (Pipeline sizes); a law row, built here, has none. */
    interestUsd?: number | null;
    interestAsOf?: string | null;
    note?: string | null;
    /** This item's part of its building's whereItStands.hearingAheadUsd. */
    hearingAheadUsd?: number | null;
    amnesty?: ItemAmnesty | null;
  };
  groupedCount: number;
  proof: { k: number; n: number } | null;
  window: {
    hearing: string | null;
    decided: string | null;
    docketed: string | null;
    issued: string | null;
    /** Court-day rolled by the server (wire *_shown); the raw dates stay server-side. */
    safeBy: string | null;
    outerBy: string | null;
    reasonBy: string | null;
  };
  maybeClosed: boolean;
  recordStatus: string | null;
  stepsId: string;
  needs: string[];
  getHelpEligible: boolean;
  matterIds: string[];
  /** reference: the case handle, "CC-123" (violation_engagement.case_reference). */
  case: {
    id: string;
    number: string;
    reference: string;
    status: string;
    /** The docket's tag (casesDocket.caseStanding); Undo leaves "you". */
    standing: "you" | "codeclear";
  } | null;
  lot: { bbl: string; buildings: { id: string; address: string }[] } | null;
  links: {
    recordHuman: string | null;
    citationPdf: string | null;
    building: string | null;
  };
  lane: string | null;
  state: RailState | null;
  /** ISO date, or the "past" / "none" gutter. */
  nodeAt: string | null;
  adviceBy: string | null;
  /** Legal last day and days to it from the advice date (Leo 10-08); the Act by tooltip. */
  legalBy?: string | null;
  cushionDays?: number | null;
  /** HPD weekend or holiday certify-by tooltip (g4). */
  actByNote?: string | null;
  /** LL33 Violation cell: "12 doors" / "Not counted". */
  recordLabel?: string | null;
  /** LL33: Who acts, and the Next line's date and DOB's (actBy is the step's date). */
  whoActs?: string | null;
  postBy?: string | null;
  postByNote?: string | null;
  owner: ItemOwner | null;
  edges: ItemEdge[];
  handoff: ItemHandoff | null;
  /** Server-set: HPD Class C, heat, window guards, HUD. */
  urgent: boolean;
  /** Boiler and DEP rows: the "about" figure (never summed), DOB's late fee, the read's day, read over 7 days old. */
  estimateUsd?: number | null;
  lateFeeUsd?: number | null;
  asOf?: string | null;
  stale?: boolean;
  /** Judgment owed (Leo 10-30): absent while the feed flag is OFF. sortBy ranks and shows while the amnesty runs. */
  judgmentOwed?: boolean;
  sortBy?: string | null;
  judgmentStep?: JudgmentStep | null;
}

/** Parts of the row Owed; they foot to `enforcementExposureUsd` (parity test). */
export interface WhereItStands {
  hearingAheadUsd: number | null;
  actByUsd: number | null;
  noDateUsd: number | null;
  notYoursUsd: number | null;
  judgmentOwedUsd?: number | null;
}

/** Filter group (`portfolio.Portfolio`, Leo 10-09); absent or null reads as "Other". */
export interface BuildingGroup {
  id: string;
  name: string;
}

/** `outside_plan` reasons (action_plan_money.py): LL84 tickets (no item, Leo 10-07), unpaid tickets with no matter
 * yet, anything else. */
export const OUTSIDE_BENCHMARKING = "benchmarking";
export const OUTSIDE_NOT_IN_PLAN = "not_in_plan";
export const OUTSIDE_NO_STEP = "no_step";
/** Money Owed by you holds that no plan item carries; [] at one_number v1. */
export interface OutsidePart {
  reason: string;
  amountUsd: number;
  count: number;
}

export interface BuildingPlan {
  address: string;
  group?: BuildingGroup | null;
  fetched: boolean;
  whereItStands: WhereItStands;
  enforcementExposureUsd: number | null;
  interestPerDayUsd: number | null;
  next: string[];
  moreCount: number;
  outsidePlan: OutsidePart[];
}

/** The feed's Debt Demolisher block: DOF's last day shown T-1, DOF's own date once, the last docket day it takes.
 * `endsBy`: the price's shown end, the wire's optional `ends_by` (DOF's Sun Jan 10 on Jan 9-10, read only with the
 * flag on), else `payBy`. */
export interface FeedAmnesty {
  payBy: string;
  endsBy: string;
  lawLine: string;
  docketedBy: string | null;
}

export interface ActionPlanFeed {
  holidays: string[];
  /** unread: HPD only, building ids HPD was not read for ("HPD not read", never clean). */
  lanes: { key: string; label: string; count: number; unread?: string[] }[];
  /** `papers` (open only): its HPD papers rows, which the legend names apart. */
  rail: { state: RailState; count: number; papers?: number }[];
  asOf: string | null;
  today: string;
  formulaVersion: string;
  items: ActionItem[];
  later: ActionItem[];
  buildings: Record<string, BuildingPlan>;
  excluded: { notYours: number };
  fetchState: {
    buildingsNotFetched: { id: string; address: string }[];
    componentsMissing: Record<string, number>;
  };
  /** The server counts each ticket once (one_number v2): the dollar sizes need it. */
  countedOnce: boolean;
  /** null outside the program: from Sat Jan 9, 2027 at HEAD; from Mon Jan 11 with judgmentOwed (ruling 15). */
  amnesty: FeedAmnesty | null;
  /** `portal_judgment_owed` as this feed was built (wire `judgment_owed`, Leo 10-31); absent = OFF = HEAD. */
  judgmentOwed?: true;
  /** DOF's own last day (Sun Jan 10, 2027); the price holds through it (Leo 10-30). */
  lawBy?: string | null;
}

/** The server's NY day, never the browser clock, so a page shows one day. */
export const amnestyOpen = (feed: Pick<ActionPlanFeed, "today" | "lawBy">) =>
  !!feed.today && !!feed.lawBy && feed.today <= feed.lawBy;

type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj => (isObject(v) ? v : {});
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const str = (v: unknown): string => (typeof v === "string" ? v : "");
const strOrNull = (v: unknown): string | null =>
  typeof v === "string" && v ? v : null;
const strs = (v: unknown): string[] => list(v).map(str).filter(Boolean);
const int = (v: unknown): number =>
  typeof v === "number" && Number.isFinite(v) ? v : 0;
/** Decimal strings off DRF. Anything unreadable is "not said", not zero. */
export const usdOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};
/** New keys appear only when the wire sends them, so today's feed parses as before. */
const sent = <K extends string, T>(
  o: Obj,
  wire: string,
  name: K,
  read: (v: unknown) => T,
) => (wire in o ? ({ [name]: read(o[wire]) } as { [P in K]: T }) : {});
const counts = (v: unknown): Record<string, number> =>
  Object.fromEntries(Object.entries(obj(v)).map(([k, n]) => [k, int(n)]));

const MONEY_KINDS: readonly MoneyKind[] = [
  "owed",
  "charged",
  "on_record",
  MONEY_ESTIMATE,
  "none",
];
const oneOf = <T extends string>(v: unknown, all: readonly T[]): T | null =>
  all.find((x) => x === v) ?? null;
const RAIL_STATES: readonly RailState[] = ["past", "x", "due", "open", "sent"];
const tally = (v: unknown) =>
  list(v).map((t) => ({
    key: str(obj(t).key),
    label: str(obj(t).label),
    count: int(obj(t).count),
  }));

const toHandoff = (raw: unknown): ItemHandoff | null => {
  if (!isObject(raw)) return null;
  const action = oneOf(raw.action, ["authorize", "hand", "ask"] as const);
  const state = oneOf(raw.state, [
    "none",
    "requested",
    "withdrawn",
    "engaged",
  ] as const);
  if (!action || !state) return null;
  return {
    action,
    state,
    at: strOrNull(raw.at),
    byName: strOrNull(raw.by_name),
    authorizedOn: strOrNull(raw.authorized_on),
    will: strOrNull(raw.will),
    youStill: strOrNull(raw.you_still),
    undoable:
      raw.undoable === true && action === "hand" && state === "requested",
    next: strOrNull(raw.next),
    owner:
      isObject(raw.owner) && str(raw.owner.key)
        ? { entity: str(raw.owner.entity), key: str(raw.owner.key) }
        : null,
  };
};

/** CodeClear holds it: export action_plan_export.HANDED. */
export const handLive = (h: ItemHandoff | null): boolean =>
  h?.action === "hand" && (h.state === "requested" || h.state === "engaged");

const toAmnesty = (raw: unknown): ItemAmnesty | null =>
  isObject(raw)
    ? {
        priceUsd: usdOrNull(raw.price_usd),
        certificateUsd: usdOrNull(raw.certificate_usd),
        priced: int(raw.priced),
        unknown: int(raw.unknown),
      }
    : null;

// "CodeClear has it" only on a live hand; a server slip falls back to "you".
const toOwner = (raw: unknown, h: ItemHandoff | null): ItemOwner | null => {
  const owner = oneOf(raw, ["you", "codeclear", "agency"] as const);
  return owner === "codeclear" && !handLive(h) ? "you" : owner;
};

// `on`: the feed's own judgment_owed flag. Off, its keys are dropped, so FE surfaces read HEAD; the export,
// notice_plan and parity scan never pass here and need the server's own gate (2A).
const toItem = (raw: unknown, on: boolean): ActionItem | null => {
  const i = obj(raw);
  const j = on ? i : {};
  const key = str(i.key);
  if (!key) return null;
  const m = obj(i.money);
  const w = obj(i.window);
  const handoff = toHandoff(i.handoff);
  const proof = isObject(i.proof)
    ? { k: int(i.proof.k), n: int(i.proof.n) }
    : null;
  const c = isObject(i.case) ? i.case : null;
  const lot = isObject(i.lot) ? i.lot : null;
  const links = obj(i.links);
  const kind = MONEY_KINDS.find((k) => k === m.kind) ?? "none";
  return {
    key,
    kind: str(i.kind),
    buildingId: str(i.building_id),
    address: str(i.address),
    agency: str(i.agency),
    agencyCounts: counts(i.agency_counts),
    recordIds: strs(i.record_ids),
    recordNoun: str(i.record_noun) || "ticket",
    boilerIds: strs(i.boiler_ids),
    action: str(i.action),
    subject: str(i.subject),
    subjects: strs(i.subjects),
    actBy: strOrNull(i.act_by),
    actByCalendar: strOrNull(i.act_by_calendar),
    actByLabel: strOrNull(i.act_by_label),
    est: i.est === true,
    money: {
      kind,
      usd: usdOrNull(m.usd),
      chargedUsd: usdOrNull(m.charged_usd),
      interestPerDayUsd: usdOrNull(m.interest_per_day_usd),
      interestUsd: usdOrNull(m.interest_usd),
      interestAsOf: strOrNull(m.interest_as_of),
      note: strOrNull(m.note),
      hearingAheadUsd: usdOrNull(m.hearing_ahead_usd),
      amnesty: toAmnesty(m.amnesty),
    },
    groupedCount: int(i.grouped_count) || 1,
    proof: proof && proof.n > 0 ? proof : null,
    window: {
      hearing: strOrNull(w.hearing),
      decided: strOrNull(w.decided),
      docketed: strOrNull(w.docketed),
      issued: strOrNull(w.issued),
      safeBy: strOrNull(w.safe_by_shown),
      outerBy: strOrNull(w.outer_by_shown),
      reasonBy: strOrNull(w.reason_by_shown),
    },
    maybeClosed: i.maybe_closed === true,
    recordStatus: strOrNull(i.record_status),
    stepsId: str(i.steps_id),
    needs: strs(i.needs),
    getHelpEligible: i.get_help_eligible === true,
    matterIds: strs(i.matter_ids),
    case: c
      ? {
          id: str(c.id),
          number: str(c.number),
          reference: str(c.reference) || `Case ${str(c.number)}`,
          status: str(c.status),
          standing: c.standing === "codeclear" ? "codeclear" : "you",
        }
      : null,
    lot: lot
      ? {
          bbl: str(lot.bbl),
          buildings: list(lot.buildings).map((b) => ({
            id: str(obj(b).id),
            address: str(obj(b).address),
          })),
        }
      : null,
    links: {
      recordHuman: strOrNull(links.record_human),
      citationPdf: strOrNull(links.citation_pdf),
      building: strOrNull(links.building),
    },
    lane: strOrNull(i.lane),
    state: oneOf(i.state, RAIL_STATES),
    nodeAt: strOrNull(i.node_at),
    adviceBy: strOrNull(i.advice_by),
    legalBy: strOrNull(i.legal_by),
    cushionDays: Number.isInteger(i.cushion_days)
      ? (i.cushion_days as number)
      : null,
    actByNote: strOrNull(i.act_by_note),
    recordLabel: strOrNull(i.record_label),
    whoActs: strOrNull(i.who_acts),
    postBy: strOrNull(i.post_by),
    postByNote: strOrNull(i.post_by_note),
    estimateUsd: usdOrNull(i.estimate_usd),
    lateFeeUsd: usdOrNull(i.late_fee_usd),
    asOf: strOrNull(i.as_of),
    stale: i.stale === true,
    owner: toOwner(i.owner, handoff),
    edges: list(i.edges)
      .map(obj)
      .filter((e) => e.kind === "leg_next" && str(e.to))
      .map((e) => ({
        to: str(e.to),
        kind: "leg_next" as const,
        label: str(e.label),
      })),
    handoff,
    urgent: i.urgent === true,
    ...sent(j, JUDGMENT_OWED_KEY, "judgmentOwed", (v) => v === true),
    ...sent(j, "sort_by", "sortBy", strOrNull),
    ...sent(j, "judgment_step", "judgmentStep", (v) =>
      oneOf(v, JUDGMENT_STEPS),
    ),
  };
};

const items = (v: unknown, on: boolean): ActionItem[] =>
  list(v)
    .map((r) => toItem(r, on))
    .filter((x): x is ActionItem => x !== null);

const toBuilding = (raw: unknown, on: boolean): BuildingPlan => {
  const b = obj(raw);
  const s = obj(b.where_it_stands);
  const g = obj(b.group);
  return {
    address: str(b.address),
    group:
      str(g.id) && str(g.name) ? { id: str(g.id), name: str(g.name) } : null,
    fetched: b.fetched !== false,
    whereItStands: {
      hearingAheadUsd: usdOrNull(s.hearing_ahead_usd),
      actByUsd: usdOrNull(s.act_by_usd),
      noDateUsd: usdOrNull(s.no_date_usd),
      notYoursUsd: usdOrNull(s.not_yours_usd),
      ...sent(on ? s : {}, "judgment_owed_usd", "judgmentOwedUsd", usdOrNull),
    },
    enforcementExposureUsd: usdOrNull(b.enforcement_exposure_usd),
    interestPerDayUsd: usdOrNull(b.interest_per_day_usd),
    next: strs(b.next),
    moreCount: int(b.more_count),
    outsidePlan: list(b.outside_plan).flatMap((p) => {
      const amountUsd = usdOrNull(obj(p).amount_usd);
      return str(obj(p).reason) && amountUsd !== null
        ? [{ reason: str(obj(p).reason), amountUsd, count: int(obj(p).count) }]
        : [];
    }),
  };
};

/** Allow-list: a key not named here is dropped. Snake on the wire (rawResponse). */
export const actionPlanFeedSchema = z
  .unknown()
  .transform((raw): ActionPlanFeed => {
    const body = obj(raw);
    const fetchState = obj(body.fetch_state);
    const on = body[JUDGMENT_OWED_KEY] === true;
    return {
      holidays: strs(body.holidays),
      lanes: list(body.lanes).map((t) => {
        const unread = strs(obj(t).unread);
        return { ...tally([t])[0], ...(unread.length ? { unread } : {}) };
      }),
      rail: list(body.rail).flatMap((r) => {
        const state = oneOf(obj(r).state, RAIL_STATES);
        const papers = int(obj(r).papers);
        return state
          ? [{ state, count: int(obj(r).count), ...(papers ? { papers } : {}) }]
          : [];
      }),
      asOf: strOrNull(body.as_of),
      today: str(body.today),
      formulaVersion: str(body.formula_version),
      items: items(body.items, on),
      later: items(body.later, on),
      buildings: Object.fromEntries(
        Object.entries(obj(body.buildings)).map(([id, b]) => [
          id,
          toBuilding(b, on),
        ]),
      ),
      excluded: { notYours: int(obj(body.excluded).not_yours) },
      fetchState: {
        buildingsNotFetched: list(fetchState.buildings_not_fetched).map(
          (b) => ({
            id: str(obj(b).id),
            address: str(obj(b).address),
          }),
        ),
        componentsMissing: counts(fetchState.components_missing),
      },
      countedOnce: body.counted_once === true,
      amnesty:
        isObject(body.amnesty) && str(body.amnesty.pay_by)
          ? {
              payBy: str(body.amnesty.pay_by),
              endsBy: (on && str(body.amnesty.ends_by)) || str(body.amnesty.pay_by),
              lawLine: str(body.amnesty.law_line),
              docketedBy: strOrNull(body.amnesty.docketed_by),
            }
          : null,
      ...(on ? { judgmentOwed: true as const } : {}),
      ...sent(on ? body : {}, "law_by", "lawBy", strOrNull),
    };
  });

/** Every feed key's prefix: a write the feed serves invalidates this. */
export const ACTION_PLAN_ROOT = ["portal", "action-plan"] as const;

/** For writes outside the feed (LL33). Not awaited: no receipt or Save waits on the feed's GET. */
export const rereadActionPlan = (
  client: QueryClient,
  refetchType: "active" | "none" = "active",
) => {
  client.invalidateQueries({ queryKey: ACTION_PLAN_ROOT, refetchType });
};

/** The features that change what the feed serves. Trap: a feed flag added to the wire goes here; codes_ switches and
 * env vars are not on the wire, so their flips are not in the key. */
export const FEED_FLAGS = [
  "judgmentOwed",
  "boilerFlow",
  "hpdExposureLane",
] as const satisfies readonly (keyof PortalFeatures)[];
/** No features read yet; never persisted. */
export const FLAGS_UNREAD = "-";
export const feedStamp = (f?: PortalFeatures): string =>
  f ? FEED_FLAGS.map((k) => (f[k] ? "1" : "0")).join("") : FLAGS_UNREAD;
/** Reads the cached features; sends no fetch. */
export const useFeedStamp = (): string =>
  feedStamp(usePortalFeaturesQuery(false).data);

/** A persisted feed built under another flag set is another key, so it never paints (M7, 10-31). */
export const actionPlanQueryKey = (
  buildingId: string | undefined,
  stamp: string,
) => [...ACTION_PLAN_ROOT, buildingId ?? "all", stamp] as const;

/** The cold Related feed runs 1.1-1.2 s locally (DE, 10-31); past this a stalled read is an error, not a spinner. */
export const FEED_TIMEOUT_MS = 20_000;
export const FEED_STALE_MS = 60_000;
/** action_plan.py:36,40 (flag off, or over MAX_LIVE_BUILDINGS) and :45 (an id not the company's), in `error`. */
export const FEED_OFF_BODY = "Not found";
export const FEED_NOT_YOURS_BODY = "Building not found";

export const fetchActionPlanFeed = (
  buildingId?: string,
): Promise<ActionPlanFeed> =>
  codecGet(
    "tracking/portal/action-plan/",
    actionPlanFeedSchema,
    buildingId
      ? { params: { building: buildingId }, timeout: FEED_TIMEOUT_MS }
      : { timeout: FEED_TIMEOUT_MS },
  );

/** Queries this tab fetched; anything else with data was restored (persister, hydration). Per client by construction:
 * a new client or clear() makes new Query objects. Trap: hydrate() over an existing Query keeps it in this set. */
const fetchedHere = new WeakSet<object>();
const cached = (client: QueryClient, key: readonly unknown[]) =>
  client.getQueryCache().find({ queryKey: key, exact: true });

/** One key for the page and the Buildings row panel. 404 = flag off or not yours. `keepPrevious` keeps any earlier
 * key's feed as a placeholder (the page's picks); without it only the same building's (a flag flip). */
export const useActionPlanFeed = (
  buildingId?: string,
  { keepPrevious = false }: { keepPrevious?: boolean } = {},
): UseQueryResult<ActionPlanFeed> => {
  const stamp = useFeedStamp();
  const client = useQueryClient();
  const id = buildingId ?? "all";
  React.useEffect(() => {
    if (stamp === FLAGS_UNREAD) return;
    client.removeQueries({
      queryKey: ACTION_PLAN_ROOT,
      predicate: (q) => q.queryKey[3] !== stamp && q.getObserversCount() === 0,
    });
  }, [client, stamp]);
  return useQuery({
    queryKey: actionPlanQueryKey(buildingId, stamp),
    queryFn: async ({ client: c, queryKey }) => {
      const feed = await fetchActionPlanFeed(buildingId);
      const q = cached(c, queryKey);
      if (q) fetchedHere.add(q);
      return feed;
    },
    // Resolved per query (queryObserver.js): a restored feed is stale at once, so a reload always rereads.
    staleTime: (q) => (fetchedHere.has(q) ? FEED_STALE_MS : 0),
    placeholderData: keepPrevious
      ? keepPreviousData
      : (prev, pq) => (pq?.queryKey[2] === id ? prev : undefined),
    meta: { persist: !buildingId && stamp !== FLAGS_UNREAD },
    retry: false,
  });
};

/** Where the feed on screen came from: `restored` (persisted, not yet reread here), `invalid` (a write's reread). */
export const feedOrigin = (
  client: QueryClient,
  q: Pick<
    UseQueryResult<ActionPlanFeed>,
    "data" | "isPlaceholderData" | "isFetching"
  >,
  buildingId: string | undefined,
  stamp: string,
) => {
  const c = cached(client, actionPlanQueryKey(buildingId, stamp));
  return {
    restored:
      q.data !== undefined &&
      !q.isPlaceholderData &&
      !!c &&
      !fetchedHere.has(c),
    invalid: q.isFetching && !!c?.state.isInvalidated,
  };
};
